//@ts-nocheck
import React from 'react'
import { NavigationPanel as BaseNavigationPanel } from 'loankeeper-common/components'
import translate from 'loankeeper-common/helpers/language'

import withAuthorized from 'shared/containers/withAuthorized'
import { useUserConfig, useSystemConfig } from 'shared'
import { createApplicationAddNavigationItem } from 'shared/ApplicationFactory'
import { createUserConfigNavigationItem } from '../UserConfigForm'
import { createChangePasswordNavigationItem } from '../ChangePasswordForm'
import { createActiveUsersNavigationItem } from '../ActiveUsersForm'
import { createClientAddNavigationItem } from '../ClientAdd'
// import { createClientSearchNavigationItem } from '../shared/ClientSearch'
import { createClientSearchNavigationItem } from '../shared/ClientSearch'
import { createClientAmlItem } from '../AMLClient'
import { createApplicationSearchNavigationItem } from '../shared/ApplicationSearch'
import { createBICNavigationItem } from '../BICForm'
import { createEEOptimalBICNavigationItem } from '../BICFormEEOptimal'
import { createLoanSearchModalNavigationItem } from '../ApplicationSearchModal'
import { createLoanSearchNavigationItem } from '../LoanSearch'
import { createLoanCalculatorNavigationItem } from '../LoanCalculator'
import { createPawnshopNavigationItem } from '../PawnshopForm'
import { createCollateralFormNavigationItem } from '../CollateralForm'
import { createCollateralSearchNavigationItem } from '../CollateralSearch'
import { createKissesNavigationItem } from '../CollateralManagementForm'
import { createApexCreditPurchaseNavigationItem } from '../ApexCreditPurchase'
import { createUCCQueueNavigationItem } from '../UCCQueue'
import { createCCYRatesFormNavigationItem } from '../CCYRates'
import { createOperationDayNavigationItem } from '../OperationDay'
import { createBlockDaysNavigationItem } from '../BlockDays'
import { createWorkPeriodNavigationItem } from '../WorkPeriodForm'
import { createUsersAndPermissionsNavigationItem } from '../UsersAndPermissions'
import { createAccountAddNavigationItem } from '../AccountForm'
import {
  createMemorialOrderAddNavigationItem,
  createOrderCashInAddNavigationItem,
  createOrderCashOutAddNavigationItem,
  createConvertOnAccountNavigationItem,
  createConvertOnCashboxNavigationItem,
} from '../OrderAdd'
import { createAccountSearchNavigationItem } from '../AccountSearch'
import { createUserReportsNavigationItem } from '../Reporting'
import { createOrderSearchNavigationItem } from '../OrderSearch'
import { createTurnoverNavigationItem } from '../Turnover'
import { createUCCPayNavigationItem } from '../UCCPayForm'
// import { createTemplatesNavigationItem } from '../Templates'
import { createAboutNavigationItem } from '../About'
import { createLogSearchNavigationItem } from '../LogSearch'
import LoanAdd from '../LoanAdd'
import LoanCover from '../LoanCover'

import { useModalActions, useHistoryPush, usePermissionCheck } from './navigationHooks'
import ClientValidationResults from '../shared/Modals/ClientValidationResults'
import { logout, lock } from '../../store/auth'
import { useSelector } from 'react-redux'

const translateLKMessage = (key: string): string => translate(`LKMESSAGE-${key}`)
const translator = (key: string): string => translate('frm_Main', key)

const NavigationPanel = () => {
  const company = useSelector((state) => state?.userConfig?.config?.system?.CompanyAlias)
  const { dispatch, openModal, openErrorModal } = useModalActions()

  const pushToHistory = useHistoryPush()

  const { permissions, checkPermission } = usePermissionCheck()

  const { isAccountEnabled }: { isAccountEnabled: string } = useSystemConfig(['AccEnabled as isAccountEnabled'])

  const { ManagerID }: { ManagerID: number } = useUserConfig(['ManagerID'])

  const navigationDropdowns = React.useMemo(
    () => [
      {
        icon: 'lk',
        items: [
          createUserConfigNavigationItem(translator, openModal),
          createChangePasswordNavigationItem(null, (modalName: string) => openModal(modalName, { force: false })),
          createActiveUsersNavigationItem(translator, openModal),
          {
            icon: 'lock',
            text: translator('mnu_file_lock'),
            onClick: () => dispatch(lock()),
          },
          {
            icon: 'cancel',
            text: translator('mnu_file_exit'),
            onClick: () =>
              dispatch(
                logout({
                  hard: true,
                }),
              ),
          },
        ],
      },
      {
        icon: 'client',
        text: translator('mnu_client'),
        items: [
          permissions.ADD_CLIENT ? createClientAddNavigationItem(translator, openModal) : undefined,
          createClientSearchNavigationItem(translator, pushToHistory),
          createClientAmlItem(translator, pushToHistory),
        ],
      },
      {
        icon: 'application',
        text: translator('mnu_application'),
        items: [
          // TODO: refactor this after QuickAccessToolbar
          createApplicationAddNavigationItem(translator, (openApplicationAdd: () => void) =>
            dispatch(openApplicationAdd()),
          ),
          createApplicationSearchNavigationItem(translator, pushToHistory),
          ManagerID !== 0 &&
            (company === 'CMPN' || company === 'ELTL') &&
            createBICNavigationItem(translator, (modalName: string) => {
              const perm = [
                'ADD_CLIENT',
                'VIEW_CLIENT',
                'ADD_APPLICATION',
                'VIEW_APPLICATION',
                'VIEW_LOAN',
                'LOAN_DISBURSE',
                'LOAN_COVER_OPER',
                'BIC_WIZARD',
                'VIEW_CLIENT_TERRORIST',
              ].find((p) => !permissions[p])

              if (perm) {
                openErrorModal(`${translateLKMessage('ERR_ACCESS_DENIED')} - ${perm}`)
              } else {
                openModal(modalName)
              }
            }),
          ManagerID !== 0 &&
            (company === 'CMPN' || company === 'EEOPT' || company === 'DEMOEEOPT') &&
            createEEOptimalBICNavigationItem(translator, (modalName: string) => {
              const perm = [
                'ADD_CLIENT',
                'VIEW_CLIENT',
                'ADD_APPLICATION',
                'VIEW_APPLICATION',
                'VIEW_LOAN',
                'LOAN_DISBURSE',
                'LOAN_COVER_OPER',
                'BIC_WIZARD',
                'VIEW_CLIENT_TERRORIST',
              ].find((p) => !permissions[p])

              if (perm) {
                openErrorModal(`${translateLKMessage('ERR_ACCESS_DENIED')} - ${perm}`)
              } else {
                openModal(modalName)
              }
            }),
        ],
      },
      {
        icon: 'loan',
        text: translator('mnu_loans'),
        items: [
          createLoanSearchModalNavigationItem(translator, openModal),
          createLoanSearchNavigationItem(translator, pushToHistory),
          createLoanCalculatorNavigationItem(translator, pushToHistory),
          ManagerID !== 0 &&
            createPawnshopNavigationItem(translator, (modalName: string) => {
              const perm = [
                'ADD_CLIENT',
                'VIEW_CLIENT',
                'ADD_COLLATERAL',
                'VIEW_COLLATERAL',
                'COLLATERAL_VISA',
                'LOAN_ATTACH_COLL',
                'ADD_APPLICATION',
                'VIEW_APPLICATION',
                'ADD_COMITTEE_DECISION',
                'VIEW_LOAN',
                'AUTO_LOAN_VISA_LMB',
                'LOAN_VISA_LMB',
                'LOAN_DISBURSE',
                'VIEW_CLIENT_TERRORIST',
              ].find((i) => !permissions[i])

              if (perm) {
                openErrorModal(`${translateLKMessage('ERR_ACCESS_DENIED')} - ${perm}`)
              } else {
                openModal(modalName)
              }
            }),
        ],
      },
      {
        icon: 'collateral',
        text: translator('mnu_coll'),
        items: [
          permissions.ADD_COLLATERAL ? createCollateralFormNavigationItem(translator, openModal) : undefined,
          createCollateralSearchNavigationItem(translator, pushToHistory),
          createKissesNavigationItem(translator, openModal),
          (company === 'CMPN' || company === 'APXD') &&
            createApexCreditPurchaseNavigationItem(translator, openModal, permissions),
        ],
      },
      {
        icon: 'clock',
        text: translator('mnu_other'),
        items: [
          // createUCCQueueNavigationItem(translator, pushToHistory),
          createCCYRatesFormNavigationItem(translator, openModal),
          createOperationDayNavigationItem(translator, openModal),
          createBlockDaysNavigationItem(translator, openModal),
        ],
      },
      // {
      //   icon: 'chart',
      //   text: translator('mnu_reports'),
      //   items: [
      //     {
      //       icon: 'decrease',
      //       text: translator('mnu_reports_portfolio_analysis'),
      //     },
      //   ],
      // },
      {
        icon: 'settings',
        text: translator('mnu_settings'),
        items: [
          // {
          //   icon: 'gift',
          //   text: translator('mnu_loans_product'),
          // },
          createWorkPeriodNavigationItem(translator, openModal),
          (permissions.VIEW_GROUP || permissions.VIEW_USER || permissions.IPACCESS_VIEW) &&
            createUsersAndPermissionsNavigationItem(translator, openModal),
        ],
      },
      {
        icon: 'liability',
        text: translator('mnu_liabilities'),
        items: [
          {
            icon: 'liability_add',
            text: translator('mnu_lb_add'),
          },
        ],
      },
      isAccountEnabled === '1' && {
        icon: 'accounting',
        text: translator('mnu_accounting'),
        items: [
          createAccountAddNavigationItem(translator, openModal),
          {
            icon: 'entry_add',
            text: translator('mnu_acc_add'),
            oneOfPermissions: [
              'ACC_ENTRIES_ADD_CASHIN',
              'ACC_ENTRIES_ADD_CASHOUT',
              'ACC_ENTRIES_ADD_CONV',
              'ACC_ENTRIES_ADD_CONV_CASH',
              'ACC_ENTRIES_ADD_MEMORDER',
            ],
            items: [
              createMemorialOrderAddNavigationItem(translator, openModal),
              createOrderCashInAddNavigationItem(translator, openModal),
              createOrderCashOutAddNavigationItem(translator, openModal),
              createConvertOnAccountNavigationItem(translator, openModal),
              createConvertOnCashboxNavigationItem(translator, openModal),
            ],
          },
          createAccountSearchNavigationItem(translator, pushToHistory),
          createUserReportsNavigationItem(translator, pushToHistory),
          createOrderSearchNavigationItem(translator, pushToHistory),
          createTurnoverNavigationItem(translator, pushToHistory),
          // createUCCPayNavigationItem(translator, openModal),
          // createTemplatesNavigationItem(translator, pushToHistory),
        ],
      },
      {
        icon: 'help',
        text: translator('mnu_about'),
        items: [
          createAboutNavigationItem(translator, pushToHistory),
          createLogSearchNavigationItem(translator, pushToHistory),
        ],
      },
    ],
    [openModal, pushToHistory, dispatch, ManagerID, isAccountEnabled, openErrorModal, permissions],
  )

  return (
    <>
      <LoanAdd />
      <LoanCover name="LOAN_COVER_DISBURSE" />
      <ClientValidationResults />
      <BaseNavigationPanel dropdowns={navigationDropdowns} checkPermission={checkPermission} />
    </>
  )
}

export default withAuthorized(NavigationPanel)
